// ChatVisibilityModal.jsx — Chat privacy 3e. ONE modal, two modes, three entry points (the nav
// defaults gear, the chat-header visibility icon, and the per-row kebab all mount THIS component):
//
//   mode="chat"     — change ONE chat's visibility + share set. Given {id, visibility, sharedUserIds,
//                     creator}. Save → PATCH /api/agent/companies/:slug/chats/:id, then dispatch the
//                     window event 'vesta:chat:visibility-changed' {id, visibility} so the nav refreshes
//                     its badges (reuses the existing 'vesta:nav:chat-created' window-event pattern).
//   mode="defaults" — the per-user default for NEW chats. Save → PATCH /api/agent/chat_preferences.
//
// Renders NOTHING when closed (return null) so at most one modal node is ever in the DOM — the header
// icon and the gear open the SAME component, and the G-VISUAL "single testid" assertion holds.
//
// Server is the sole authority (the modal is convenience): the PATCH re-checks manage rights (creator
// or platform admin), the visibility allowlist, and that every share id is a company member.

import React, { useCallback, useEffect, useMemo, useState } from "react"

const VISIBILITIES = ["private", "shared", "company"]

// Inline glyphs (Lucide-style, stroke currentColor) so this file has no icon-import coupling.
function Glyph({ name, size = 16 }) {
  const base = {
    width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor",
    strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true",
  }
  const paths = {
    lock: (<><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>),
    people: (<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>),
    building: (<><rect x="4" y="2" width="16" height="20" rx="2" /><path d="M9 22v-4h6v4M9 6h.01M15 6h.01M9 10h.01M15 10h.01M9 14h.01M15 14h.01" /></>),
    x: (<><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>),
  }
  return <svg {...base}>{paths[name] || null}</svg>
}

// The visibility glyph name for a chat row / header icon (company → building; but the nav BADGE shows
// nothing for company — that decision lives at the call site, not here).
export function visibilityGlyph(v) {
  return v === "private" ? "lock" : v === "shared" ? "people" : "building"
}

const RADIO_COPY = {
  chat: {
    private: { label: "Private", sub: "Only you can see this chat." },
    shared: { label: "Specific people", sub: "You and the people you choose." },
    company: { label: "Company-wide", sub: "Everyone at your company." },
  },
  defaults: {
    private: { label: "Private", sub: "Only you can see chats you start." },
    shared: { label: "Specific people", sub: "You choose who can see each new chat." },
    company: { label: "Company-wide", sub: "Everyone at your company." },
  },
}

export default function ChatVisibilityModal({ open, mode, chat, companySlug, onClose, onSaved }) {
  const isChat = mode === "chat"
  const initialVisibility = isChat ? (chat && chat.visibility) || "private" : (chat && chat.defaultVisibility) || "private"

  const [visibility, setVisibility] = useState(initialVisibility)
  const [selectedIds, setSelectedIds] = useState(() => (chat && Array.isArray(chat.sharedUserIds) ? chat.sharedUserIds.map(String) : []))
  const [members, setMembers] = useState(null) // null until fetched (chat mode)
  const [membersError, setMembersError] = useState(false) // true = fetch failed (visible state + Retry)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  // Re-seed local state whenever a fresh chat/default is opened (the same modal instance is reused).
  useEffect(() => {
    if (!open) return
    setVisibility(initialVisibility)
    setSelectedIds(chat && Array.isArray(chat.sharedUserIds) ? chat.sharedUserIds.map(String) : [])
    setError(null)
    setMembersError(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, chat])

  // Load the person-picker source EAGERLY when the modal opens in chat mode (QA 2026-07-23 bug B —
  // was lazily gated on visibility === "shared", so the list appeared only in a post-click fetch
  // window; now the members are already in hand the instant "Specific people" is selected).
  // A failed fetch is a VISIBLE state (membersError → "Couldn't load people." + Retry), never a
  // silently-empty picker box — the old `r.ok ? … : { members: [] }` swallowed every failure into
  // an empty list that rendered as a bare border with nothing to select.
  useEffect(() => {
    if (!open || !isChat || members != null || membersError) return
    let cancelled = false
    fetch(`/api/agent/companies/${companySlug}/chat_members`, { headers: { Accept: "application/json" } })
      .then((r) => { if (!r.ok) throw new Error(`chat_members ${r.status}`); return r.json() })
      .then((d) => { if (!cancelled) setMembers(Array.isArray(d.members) ? d.members : []) })
      .catch(() => { if (!cancelled) setMembersError(true) })
    return () => { cancelled = true }
  }, [open, isChat, members, membersError, companySlug])

  // Retry after a failed members fetch: clearing the error flag re-arms the effect above.
  const retryMembers = useCallback(() => setMembersError(false), [])

  const creatorId = chat && chat.creator && chat.creator.id != null ? String(chat.creator.id) : null

  const toggleMember = useCallback((id) => {
    const sid = String(id)
    if (sid === creatorId) return // DD-8: creator is pinned, never toggled off
    setSelectedIds((prev) => (prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid]))
  }, [creatorId])

  const save = useCallback(async () => {
    setSaving(true)
    setError(null)
    try {
      let resp
      if (isChat) {
        const body = { visibility }
        if (visibility === "shared") body.shared_user_ids = selectedIds.map(Number)
        resp = await fetch(`/api/agent/companies/${companySlug}/chats/${chat.id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrfToken() },
          body: JSON.stringify(body),
        })
      } else {
        resp = await fetch(`/api/agent/chat_preferences`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrfToken() },
          body: JSON.stringify({ default_chat_visibility: visibility }),
        })
      }
      if (!resp.ok) { setError("Couldn’t save. Try again."); setSaving(false); return }
      if (isChat) {
        window.dispatchEvent(new CustomEvent("vesta:chat:visibility-changed", { detail: { id: String(chat.id), visibility } }))
      }
      setSaving(false)
      if (onSaved) onSaved({ visibility })
      if (onClose) onClose()
    } catch (_e) {
      setError("Couldn’t save. Try again.")
      setSaving(false)
    }
  }, [isChat, visibility, selectedIds, companySlug, chat, onSaved, onClose])

  const copy = useMemo(() => RADIO_COPY[isChat ? "chat" : "defaults"], [isChat])

  if (!open) return null

  return (
    <div className="cvm-overlay" data-testid="chat-visibility-modal" role="dialog" aria-modal="true" aria-label={isChat ? "Chat sharing & visibility" : "Default chat visibility"}>
      <div className="cvm-backdrop" onClick={onClose} aria-hidden="true" />
      <div className="cvm-panel" role="document">
        <div className="cvm-head">
          <h2 className="cvm-title">{isChat ? "Sharing & visibility" : "Default for new chats"}</h2>
          <button type="button" className="cvm-x" onClick={onClose} aria-label="Close"><Glyph name="x" /></button>
        </div>

        {!isChat && <p className="cvm-copy">Applies to chats you start from now on.</p>}

        <div className="cvm-radios" role="radiogroup">
          {VISIBILITIES.map((v) => (
            <label key={v} className={`cvm-radio${visibility === v ? " is-selected" : ""}`}>
              <input
                type="radio"
                name="chat-visibility"
                value={v}
                checked={visibility === v}
                onChange={() => setVisibility(v)}
              />
              <span className="cvm-radio__ico"><Glyph name={visibilityGlyph(v)} /></span>
              <span className="cvm-radio__text">
                <span className="cvm-radio__label">{copy[v].label}</span>
                <span className="cvm-radio__sub">{copy[v].sub}</span>
              </span>
            </label>
          ))}
        </div>

        {isChat && visibility === "shared" && (
          <div className="cvm-picker" data-testid="chat-visibility-picker">
            {membersError ? (
              // QA 2026-07-23 bug B: a failed fetch must SAY so (and offer Retry), not render an
              // empty box that looks like the picker never expanded. cvm-cancel is this modal's
              // text-sized neutral-secondary (b7ba01da); the __retry modifier only compacts it —
              // never a bare <button>.
              <div className="cvm-picker__error" role="alert">
                <span>Couldn’t load people.</span>
                <button type="button" className="cvm-cancel cvm-picker__retry" onClick={retryMembers}>Retry</button>
              </div>
            ) : members == null ? (
              <div className="cvm-picker__loading">Loading people…</div>
            ) : members.length === 0 ? (
              <div className="cvm-picker__empty">No other people in this company yet.</div>
            ) : (
              members.map((m) => {
                const mid = String(m.id)
                const isCreator = mid === creatorId
                const checked = isCreator || selectedIds.includes(mid)
                return (
                  <label key={mid} className={`cvm-person${isCreator ? " is-creator" : ""}`}>
                    <input type="checkbox" checked={checked} disabled={isCreator} onChange={() => toggleMember(mid)} />
                    <span className="cvm-person__name">{m.name || "Unknown"}{isCreator ? " (you, owner)" : ""}</span>
                  </label>
                )
              })
            )}
          </div>
        )}

        {error && <div className="cvm-error" role="alert">{error}</div>}

        <div className="cvm-actions">
          <button type="button" className="cvm-cancel" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="pgs-approve" onClick={save} disabled={saving} data-testid="chat-visibility-save">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  )
}

// The Rails CSRF token from the meta tag (present on every authenticated page). Empty string if
// absent (dev/test) — the request specs sign in via Devise helpers and don't require it.
function csrfToken() {
  const el = typeof document !== "undefined" && document.querySelector('meta[name="csrf-token"]')
  return el ? el.getAttribute("content") || "" : ""
}
